import { MissionAssessmentRequestStatus } from '@@types/api.types';
import { Score } from '@components/shared/score.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { cn } from '@lib/utils';
import {
  useMissionAssessmentCoreQueryOptions,
  useMissionAssessmentVersionsByTechnologyQueryOptions,
} from '@queries/mission-assessments.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetTechnologyMrlAssessment, useGetTechnologyTrlAssessment } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { useQuery } from '@tanstack/react-query';
import { TechnologyHeaderTab } from '@utils/query-state.util';
import { ArrowRightIcon, ClipboardCheckIcon, ClockIcon, FileTextIcon, XCircleIcon } from 'lucide-react';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

type ReadinessSummaryProps = {
  onNavigateToTab?: (tab: TechnologyHeaderTab) => void;
};

type ScoreItemProps = {
  label: string;
  onClick?: () => void;
  score: number | null | undefined;
  variant: 'mrl' | 'trl';
};

const ScoreItem: FC<ScoreItemProps> = (props) => {
  const { label, onClick, score, variant } = props;

  return (
    <button
      className={cn(
        'flex items-start gap-2 p-2 rounded-lg transition-colors',
        onClick && 'hover:bg-gray-50 cursor-pointer',
      )}
      disabled={!onClick}
      onClick={onClick}
      type="button"
    >
      <Score
        design="box"
        score={score}
        size="small"
        type={variant === 'trl' ? 'TRL' : 'MRL'}
      />
      <div className="flex flex-col items-start">
        <span className="text-xs font-semibold text-[#3D2EFF]">{label}</span>
        <span className="text-xs font-light text-[#1F1C1AB2]">Score</span>
      </div>
    </button>
  );
};

type MaItemProps = {
  expertName?: string;
  onClick?: () => void;
  status?: MissionAssessmentRequestStatus;
  useName?: string;
};

const STATUS_CONFIG = {
  [MissionAssessmentRequestStatus.ASSESSED]: {
    badgeVariant: 'success' as const,
    Icon: ClipboardCheckIcon,
    label: 'Assessed',
  },
  [MissionAssessmentRequestStatus.CANCELLED]: {
    badgeVariant: 'destructive' as const,
    Icon: XCircleIcon,
    label: 'Cancelled',
  },
  [MissionAssessmentRequestStatus.IN_PROGRESS]: {
    badgeVariant: 'pending' as const,
    Icon: ClockIcon,
    label: 'In Progress',
  },
  [MissionAssessmentRequestStatus.REQUESTED]: {
    badgeVariant: 'primary' as const,
    Icon: FileTextIcon,
    label: 'Requested',
  },
};

const MaItem: FC<MaItemProps> = (props) => {
  const { expertName, onClick, status, useName } = props;

  const hasData = Boolean(status);
  const config = status ? STATUS_CONFIG[status] : null;
  const StatusIcon = config?.Icon;

  return (
    <button
      className={cn(
        'flex items-center gap-3 p-2 rounded-lg transition-colors w-full',
        onClick && 'hover:bg-gray-50 cursor-pointer',
      )}
      disabled={!onClick}
      onClick={onClick}
      type="button"
    >
      <div className="flex items-center justify-center w-[42px] h-[42px] rounded-md bg-amber-50 shrink-0">
        {StatusIcon ? (
          <StatusIcon
            className="text-amber-700"
            size={20}
          />
        ) : (
          <span className="text-2xl font-bold text-gray-400">?</span>
        )}
      </div>
      <div className="flex flex-col items-start gap-1 min-w-0">
        <span className="text-sm font-medium text-gray-900">Mission</span>
        {hasData && config ? (
          <div className="flex flex-col gap-1">
            <Badge
              size="xs"
              variant={config.badgeVariant}
            >
              {config.label}
            </Badge>
            {(expertName || useName) && (
              <span className="text-xs text-gray-500 truncate max-w-[120px]">
                {expertName ? `by ${expertName}` : useName}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-500">No assessment</span>
        )}
      </div>
    </button>
  );
};

export const ReadinessSummary: FC<ReadinessSummaryProps> = (props) => {
  const { onNavigateToTab } = props;
  const { technologyId } = useParams();

  const { data: technology } = useGetTechnology(technologyId);
  const { isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();

  const { data: trlAssessment, isPending: isTrlLoading } = useGetTechnologyTrlAssessment(technologyId);
  const { data: mrlAssessment, isPending: isMrlLoading } = useGetTechnologyMrlAssessment(technologyId);

  // Fetch MA versions to get the latest score
  const shouldFetchMissionAssessments =
    !isSelectedCompanyParentOrganization || Boolean(selectedCompany?.id && technology?.company?.id);
  const versionsQueryOptions = useMissionAssessmentVersionsByTechnologyQueryOptions(
    shouldFetchMissionAssessments ? technologyId : undefined,
  );
  const { data: maVersions, isPending: isMaVersionsLoading } = useQuery(versionsQueryOptions);

  const latestMaId = maVersions?.[0]?.id;
  const coreQueryOptions = useMissionAssessmentCoreQueryOptions(shouldFetchMissionAssessments ? latestMaId : undefined);
  const { data: latestMa, isPending: isMaCoreLoading } = useQuery(coreQueryOptions);

  const isLoading = isTrlLoading || isMrlLoading || isMaVersionsLoading || (Boolean(latestMaId) && isMaCoreLoading);

  const trlScore = trlAssessment?.score ?? null;
  const mrlScore = mrlAssessment?.score ?? null;

  // MA data
  const maStatus = latestMa?.request?.status;
  const maExpertName = latestMa?.expert?.profile?.fullName;
  const maUseName = latestMa?.request?.use?.name;
  const hasMa = Boolean(maStatus);

  const hasAnyScore = trlScore !== null || mrlScore !== null || hasMa;
  const canNavigate = Boolean(onNavigateToTab);

  const handleNavigateToTrl = () => {
    onNavigateToTab?.('trl');
  };

  const handleNavigateToMrl = () => {
    onNavigateToTab?.('mrl');
  };

  const handleNavigateToMa = () => {
    onNavigateToTab?.('ma');
  };

  return (
    <Tile
      className="min-h-fit"
      emptyState={{ title: 'No readiness assessments yet' }}
      header={{
        iconButton:
          hasAnyScore && canNavigate
            ? {
                icon: <ArrowRightIcon size={16} />,
                onClick: handleNavigateToTrl,
              }
            : undefined,
        title: 'Readiness Summary',
      }}
      hoverReveal={canNavigate}
      isEmpty={!hasAnyScore && !isLoading}
      isLoading={isLoading}
    >
      <div className="flex flex-wrap gap-2">
        <ScoreItem
          label="TRL"
          onClick={trlScore ? handleNavigateToTrl : undefined}
          score={trlScore}
          variant="trl"
        />
        <ScoreItem
          label="MRL"
          onClick={mrlScore ? handleNavigateToMrl : undefined}
          score={mrlScore}
          variant="mrl"
        />
        <MaItem
          expertName={maExpertName}
          onClick={hasMa ? handleNavigateToMa : undefined}
          status={maStatus}
          useName={maUseName}
        />
      </div>
    </Tile>
  );
};
